/**
 * Randomized Kids Module
 * 
 * Handles dynamic loading of randomized kids content to bypass WP Rocket cache
 * This ensures randomized content refreshes on each page load
 */

class RandomizedKids {
    constructor() {
        this.init();
    }

    init() {
        // Find all randomized kids blocks
        const randomizedBlocks = document.querySelectorAll('.kids-block[data-randomize="true"]');
        
        if (randomizedBlocks.length === 0) {
            return;
        }

        // Load randomized content for each block
        randomizedBlocks.forEach(block => {
            this.loadRandomizedContent(block);
        });
    }

    async loadRandomizedContent(block) {
        try {
            // Show loading state
            this.showLoadingState(block);

            // Get block data
            const blockId = block.dataset.blockId;
            const maxItems = parseInt(block.dataset.maxItems) || 12;
            const taxonomyFilter = block.dataset.taxonomyFilter || '';
            const nonce = block.dataset.randomizedNonce || '';

            // Prepare form data
            const formData = new FormData();
            formData.append('action', 'load_randomized_kids');
            formData.append('block_id', blockId);
            formData.append('max_items', maxItems);
            formData.append('taxonomy_filter', taxonomyFilter);
            formData.append('nonce', nonce);

            // Make AJAX request
            const response = await fetch(ajaxurl, {
                method: 'POST',
                body: formData
            });

            const result = await response.json();

            if (result.success && result.data.html) {
                // Replace the kids grid with new content
                const existingGrid = block.querySelector('.kids-grid');
                if (existingGrid) {
                    existingGrid.outerHTML = result.data.html;
                } else {
                    // If no grid exists, replace the entire block content
                    const blockContent = block.querySelector('.kids-block__content') || block;
                    blockContent.innerHTML = result.data.html;
                }

                // Hide loading state
                this.hideLoadingState(block);

                // Trigger custom event for other modules
                block.dispatchEvent(new CustomEvent('randomizedKidsLoaded', {
                    detail: { 
                        count: result.data.count,
                        blockId: result.data.block_id 
                    }
                }));

                console.log('Randomized kids content loaded successfully');
            } else {
                throw new Error('Failed to load randomized content');
            }

        } catch (error) {
            console.error('Error loading randomized kids content:', error);
            this.hideLoadingState(block);
            this.showErrorState(block);
        }
    }

    showLoadingState(block) {
        // Create loading overlay
        const loadingOverlay = document.createElement('div');
        loadingOverlay.className = 'kids-block__loading-overlay';
        loadingOverlay.innerHTML = `
            <div class="kids-block__loading-spinner">
                <div class="spinner"></div>
                <p>Loading kids...</p>
            </div>
        `;

        // Add loading styles
        const style = document.createElement('style');
        style.textContent = `
            .kids-block__loading-overlay {
                position: absolute;
                top: 0;
                left: 0;
                right: 0;
                bottom: 0;
                background: rgba(255, 255, 255, 0.9);
                display: flex;
                align-items: center;
                justify-content: center;
                z-index: 10;
            }
            .kids-block__loading-spinner {
                text-align: center;
            }
            .kids-block__loading-spinner .spinner {
                width: 40px;
                height: 40px;
                border: 4px solid #f3f3f3;
                border-top: 4px solid #E87722;
                border-radius: 50%;
                animation: spin 1s linear infinite;
                margin: 0 auto 10px;
            }
            @keyframes spin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
            }
        `;
        document.head.appendChild(style);

        // Make block relative positioned if not already
        if (getComputedStyle(block).position === 'static') {
            block.style.position = 'relative';
        }

        // Add loading overlay
        block.appendChild(loadingOverlay);
    }

    hideLoadingState(block) {
        const loadingOverlay = block.querySelector('.kids-block__loading-overlay');
        if (loadingOverlay) {
            loadingOverlay.remove();
        }
    }

    showErrorState(block) {
        const errorMessage = document.createElement('div');
        errorMessage.className = 'kids-block__error';
        errorMessage.innerHTML = `
            <p>Unable to load randomized content. Please refresh the page.</p>
        `;

        // Add error styles
        const style = document.createElement('style');
        style.textContent = `
            .kids-block__error {
                text-align: center;
                padding: 20px;
                background: #f8f9fa;
                border: 1px solid #dee2e6;
                border-radius: 6px;
                color: #6c757d;
            }
        `;
        document.head.appendChild(style);

        // Replace content with error message
        const existingGrid = block.querySelector('.kids-grid');
        if (existingGrid) {
            existingGrid.outerHTML = errorMessage.outerHTML;
        }
    }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
    new RandomizedKids();
});

// Export for potential use in other modules
if (typeof module !== 'undefined' && module.exports) {
    module.exports = RandomizedKids;
} 